/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import createSaga, { SubCategoryCreateData } from './create';
import updateSaga, { SubCategoryUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { FileData, PaginatedResponse } from '../types';
import {
  Question as IQuestion, QuestionStatus as IQuestionStatus,
  QuestionType as IQuestionType,
  QuestionUserType as IQuestionUserType,
  QuestionUserTypeTitle as IQuestionUserTypeTitle,
} from '../../hooks/api/questions';

export const moduleName = 'subCategory';

export type SubCategoryStatus = 'published' | 'deactivated' | 'draft';
export const SubCategoryStatusTitle: Record<SubCategoryStatus, string> = {
  published: 'Published',
  deactivated: 'Hidden',
  draft: 'Draft',
};

export type SubCategoryStatisticDisplayMode = 'percent' | 'dollar';
export const SubCategoryStatisticDisplayUnitLabels: Record<SubCategoryStatisticDisplayMode, string> = {
  percent: '%',
  dollar: '$',
};

export interface StatisticValue {
  value: number;
  votes: number;
}

export type StatisticValuePair = Record<SubCategoryStatisticDisplayMode, StatisticValue>

export type StatisticValueType = 'actual' | 'expected'

export interface StatisticValues {
  actual: StatisticValuePair;
  expected: StatisticValuePair;
}
export type QuestionType = IQuestionType;
export type QuestionUserType = IQuestionUserType;
export const QuestionUserTypeTitle = IQuestionUserTypeTitle;

export type QuestionStatus = IQuestionStatus;

export type Question = IQuestion;

export interface SubCategory {
  id: string;
  header: string;
  isActive: true,
  description: string;
  searchKeywords: string[];
  workersKeywords: string[];
  displayKeywords: string[];
  category: {
    id: string;
    name: string;
    isActive: true,
    header: string;
    description: string;
    author: string;
    file: FileData,
    comments: {
      id: string;
      text: string;
      status: string;
      entity: string;
      likeNumber: number
      dislikeNumber: number
      files: FileData[],
      parentComment: string;
      user: {
        id: string;
        email: string;
        name: string;
        phone: string;
        createdAt: string;
        zipCode: string;
        yearBirth: number
        description: string;
        photo: FileData
      },
      subCommentCount: number
      business: {
        id: string;
        name: string;
        description: string;
        email: string;
        zipCode: string;
        type: string;
        website: string;
        address: string;
        phone: string;
        status: 'published',
        file: FileData,
        createdAt: string;
        updatedAt: string;
        users: {
          id: string;
          email: string;
          name: string;
          phone: string;
          createdAt: string;
          zipCode: string;
          yearBirth: number
          description: string;
          photo: FileData
        }[]
      },
      category: {
        id: string;
        name: string;
        isActive: true,
        header: string;
        description: string;
        author: string;
        file: FileData
      },
      subCategory: {
        id: string;
        header: string;
        isActive: true,
        description: string;
        category: {
          id: string;
          name: string;
          isActive: true
        }
      },
      article: {
        id: string;
        name: string;
        description: string;
        status: 'published',
        tags: {
          id: string;
          name: string;
        }[],
        file: FileData,
        createdAt: string;
        updatedAt: string;
      },
      createdAt: string;
      updatedAt: string;
    }[],
    subCategories: {
      id: string;
      header: string;
      isActive: true,
      description: string;
      category: {
        id: string;
        name: string;
        isActive: true
      }
    }[]
  },
  comments: {
    id: string;
    text: string;
    status: string;
    entity: string;
    likeNumber: number
    dislikeNumber: number
    files: FileData[],
    parentComment: string;
    user: {
      id: string;
      email: string;
      name: string;
      phone: string;
      createdAt: string;
      zipCode: string;
      yearBirth: number
      description: string;
      photo: FileData
    },
    subCommentCount: number
    business: {
      id: string;
      name: string;
      description: string;
      email: string;
      zipCode: string;
      type: string;
      website: string;
      address: string;
      phone: string;
      status: 'published',
      file: FileData,
      createdAt: string;
      updatedAt: string;
      users: {
        id: string;
        email: string;
        name: string;
        phone: string;
        createdAt: string;
        zipCode: string;
        yearBirth: number
        description: string;
        photo: FileData
      }[]
    },
    category: {
      id: string;
      name: string;
      isActive: true,
      header: string;
      description: string;
      author: string;
      file: FileData
    },
    subCategory: {
      id: string;
      header: string;
      isActive: true,
      description: string;
      category: {
        id: string;
        name: string;
        isActive: true
      }
    },
    article: {
      id: string;
      name: string;
      description: string;
      status: 'published',
      tags: {
        id: string;
        name: string;
      }[],
      file: FileData;
      createdAt: string;
      updatedAt: string;
    },
    createdAt: string;
    updatedAt: string;
  }[],
  questions: Question[];
}

export interface State {
  list: BaseState<PaginatedResponse<SubCategory>>,
  byId: BaseState<SubCategory>,
  create: BaseState<SubCategoryCreateData>,
  update: BaseState<SubCategoryUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'children', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const subCategory = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = subCategory;

export function useSubCategory(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
